import browserPersistence from "@/utils/session-persistance";
import {
  FilteredProduct,
  GroupParentAttributeOptionSet,
} from "@/components/configurator/global-option/types";
import {
  QuotePrices,
  ProductPrices,
  ItemPrices,
} from "@/utils/configurator-actions/get-prices";
import Serialize from "@/utils/serialize";
import Deserialize from "@/utils/deserialize";
const sessionStorage = new browserPersistence();
import getQuotePricesByQuoteId from "@/utils/configurator-actions/get-prices";

export interface ErrorMessages {
  [groupName: string]: string[];
}

export interface GlobalOptions {
  group_name: string;
  group_key?: string;
  parent_name: string | null;
  key: string;
  value: string | null;
  isProduct?: boolean;
  label?: string | null;
  isMultiselect?: boolean;
}

export interface GlobalOptionsData {
  global_picks: GlobalOptions[];
  additionalData: FilteredProduct[];
}
export interface Attribute {
  attribute_code: string;
  attribute_value: string;
  attribute_label?:string;
}
export interface Image {
  label: string;
  url: string;
}
export interface Amount {
  currency: string;
  value: number;
}
export interface RegularPrice {
  amount: Amount;
}
export interface Price {
  regularPrice: RegularPrice;
}
export interface AdditionalData {
  name: string;
  image: Image;
  price: Price;
  sku: string;
  [key: string]: any;
}
export interface BodyUnit {
  group_name: string;
  parent_name: string;
  attributes: Attribute[];
  additionalData: {
    counterBody: AdditionalData;
    foodGuard?: AdditionalData;
  };
}
export interface BodyUnitBody {
  position: number;
  global_picks_data: GlobalOptionsData | null;
  epicor_product_id: number;
  body_picks: BodyUnit;
  additionalData?: {
    counterBody?: AdditionalData;
    foodGuard?: AdditionalData;
  };
  pricingDetails?: ProductPrices;
  isDropIn:boolean;
}
export interface DukeActiveQuote {
  id_quote: number;
  quote_name: string;
  global_picks_data: GlobalOptionsData | null | undefined;
  bodies: BodyUnitBody[] | undefined;
  salesforce_quote_id_with_suffix: string | null;
  subTotal?: number;
  totalCost?: number;
  totalPrice?: number;
}

export interface FormData {
  scope: string;
  form_data: string;
}

export const QUOTE_KEY = "DUKE_ACTIVE_QUOTE";
export const CUSTOMER_DETAILS_KEY = "DUKE_CACHE_STORAGE";
export const QUOTE_PRICE_KEY = "QUOTE_PRICES";
export const FORM_DATA_KEY = "DUKE_FORM_DATA";

const error = new Error("No record found");

export function getFormData() {
  let data: any = sessionStorage.getItem(FORM_DATA_KEY);
  let deserializedData;
  if (data) {
    deserializedData = Deserialize.groupParentAttributeOptionSet(
      data.form_data
    );
  }
  if (deserializedData) return deserializedData;
}

export function removeFormData() {
  sessionStorage.removeItem(FORM_DATA_KEY);
}

export function createFormData(scope: string, data: any) {
  let formData: FormData;
  let serializedData = Serialize.groupParentAttributeOptionSet(data);
  formData = {
    scope: scope,
    form_data: serializedData,
  };
  sessionStorage.setItem(FORM_DATA_KEY, formData);
}

export function setQuote(data: any) {
  sessionStorage.removeItem(QUOTE_KEY);
  sessionStorage.setItem(QUOTE_KEY, data);
}

function createQuote(quoteName: string) {
  let globalData: GlobalOptionsData | null = null;
  let data: DukeActiveQuote;
  data = {
    id_quote: 0,
    quote_name: quoteName,
    global_picks_data: globalData,
    bodies: [],
    salesforce_quote_id_with_suffix: null,
  };
  sessionStorage.setItem(QUOTE_KEY, data);
}

function getQuoteName() {
  let data: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (data) return data.quote_name;
  else return "";
}

function getQuoteId() {
  let data: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (data) return data.id_quote;
  else return null;
}

function setQuoteId(idQuote: number) {
  let quote: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (quote) {
    quote.id_quote = idQuote;
    setQuote(quote);
  }
}
function getSalesforceQuoteId() {
  let data: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (data) return data.salesforce_quote_id_with_suffix;
  else return null;
}

function setSalesforceQuoteId(salesforceQuoteId: string | null) {
  let quote: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (quote && salesforceQuoteId) {
    quote.salesforce_quote_id_with_suffix = salesforceQuoteId;
    setQuote(quote);
  }
}

function addGlobalOptionsData(
  value: GlobalOptions[],
  additionalData?: FilteredProduct[]
) {
  const quote: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (!quote) throw new Error("Quote not found");
  const globalPickData: GlobalOptionsData = {
    global_picks: value,
    additionalData: additionalData ?? [],
  };
  if (globalPickData) {
    quote.global_picks_data = globalPickData;
    setQuote(quote);
  }
}

export function getGlobalOptionData() {
  let data: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (data?.global_picks_data) return data.global_picks_data;
  else {
    data.global_picks_data = {
      global_picks: [],
      additionalData: [],
    };
    setQuote(data);
    return data.global_picks_data;
  }
}

function addBodyUnit(
  value: BodyUnit,
  epicor_product_id: number,
  position: number,
  id?: number,
  updatedGlobalOptionsData?: any,
  isDropIn?:boolean,
) {
  const globalData: GlobalOptionsData | null = updatedGlobalOptionsData ?? getGlobalOptionData();
  if (!globalData) throw Error("Global options not found");
  let quote: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);

  const newBodyPick: BodyUnitBody = {
    position: position,
    global_picks_data: globalData,
    epicor_product_id: epicor_product_id,
    body_picks: value,
    isDropIn: isDropIn ?? false,
  };
  if (id !== undefined) quote.bodies?.splice(id, 1, newBodyPick);
  else quote.bodies?.push(newBodyPick);
  setQuote(quote);
}

function getBodyUnitByIdx(
  idx?: number
): BodyUnitBody | BodyUnitBody[] | undefined {
  let quote: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);

  if (quote && idx !== undefined) {
    return quote.bodies?.[idx];
  }
  if (quote && idx === undefined) return quote.bodies;
  else throw error;
}

function deleteBodyUnitByIdx(idx: number) {
  let quote: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (quote.bodies) {
    let newB = [...quote.bodies];
    newB.splice(idx, 1);
    newB = newB.map((body, currentIdx) => ({
      ...body,
      position: currentIdx,
    }));
    quote.bodies = newB;
    setQuote(quote);
  } else throw error;
}

function cloneBodyUnitByIdx(
  idx: number,
  epicor_product_id: number,
  price: number,
  position: number
) {
  let quote: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (quote.bodies) {
    if (idx >= 0 && idx < quote.bodies.length) {
      const newBodyPick: BodyUnitBody = JSON.parse(
        JSON.stringify(quote.bodies[idx])
      );
      newBodyPick.epicor_product_id = epicor_product_id;
      newBodyPick.body_picks.additionalData.counterBody.price.regularPrice.amount.value =
        price;
      newBodyPick.position = position;
      quote.bodies.push(newBodyPick);
    } else throw error;
    setQuote(quote);
  }
}

function findBodyUnitIdxByEpicorId(id: number) {
  let data: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (data.bodies)
    return data.bodies.findIndex(
      (body: BodyUnitBody) => body.epicor_product_id === id
    );
  else throw error;
}

function deleteBodyUnits() {
  let data: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (data) {
    data.bodies = [];
    setQuote(data);
  }
}

function setBodyUnits(bodyUnits: BodyUnitBody[]) {
  let data: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (data) {
    data.bodies = bodyUnits;
    setQuote(data);
  }
}
function getQuote() {
  let data: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  return data ?? null;
}

function deleteQuote() {
  sessionStorage.removeItem(QUOTE_KEY);
}

function updateBodyUnit(body: BodyUnitBody) {
  let data: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  if (data && data.bodies) {
    const idx = findBodyUnitIdxByEpicorId(body.epicor_product_id);
    data.bodies[idx] = body;
    setQuote(data);
  }
}

function getPricesBySku(
  quotePrices: QuotePrices,
  idProduct: number,
  sku: string
): ItemPrices | undefined {
  const product = quotePrices?.products?.find(
    (product: ProductPrices) => product.id === idProduct
  );
  if (product?.items) {
    const item = product.items.find((item: ItemPrices) =>
      item.sku.startsWith(sku)
    );
    return item;
  }
}

function getQuotePrices(quotePrices: QuotePrices) {
  return {
    id: quotePrices?.id,
    name: quotePrices?.name,
    discountPercentage: quotePrices?.discountPercentage,
    shipping: quotePrices?.shipping,
    subtotal: quotePrices?.subtotal,
    totalCost: quotePrices?.totalCost,
    totalPrice: quotePrices?.totalPrice,
    products: quotePrices?.products,
  };
}
function getBodyPricesById(idProduct: number) {
  const quotePrices: QuotePrices =
    sessionStorageOperations.getData(QUOTE_PRICE_KEY);
  const product = quotePrices?.products.find(
    (product: ProductPrices) => product.id === idProduct
  );
  if (product) {
    return {
      id: product.id,
      price: product.price,
      fixedPrice: product.fixedPrice,
      cost: product.cost,
      fixedCost: product.fixedCost,
    };
  }
}
function postData(key: string, data: any) {
  sessionStorage.setItem(key, data);
}
function getData(key: string) {
  return sessionStorage.getItem(key);
}
function deleteDataByKey(key: string) {
  sessionStorage.removeItem(key);
}
function getLoggedInCustomerDetails() {
  return sessionStorageOperations.getData(CUSTOMER_DETAILS_KEY);
}
function deleteLoggedInCustomerDetails() {
  sessionStorageOperations.deleteDataByKey(CUSTOMER_DETAILS_KEY);
}
function setLoggedInnCustomerDetails(custometDetails: any) {
  sessionStorageOperations.postData(CUSTOMER_DETAILS_KEY, custometDetails);
}
function deleteQuotePrices() {
  sessionStorage.removeItem(QUOTE_PRICE_KEY);
}

function updateDukeActiveQuoteBodyPosition(
  newPosition: number,
  prevPosition: number
) {
  let quote: DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);
  let bodies = quote.bodies ?? [];
  if (bodies) {
    const element = bodies.splice(prevPosition, 1)[0];
    bodies.splice(newPosition, 0, element);
    bodies = bodies.map((body) => {
      body.position = bodies.indexOf(body);
      return body;
    });
  }
  quote.bodies = bodies;
  setQuote(quote);
}

async function updateQuotePrices(quoteId: number, quote?: any) : Promise<DukeActiveQuote> {

  if(!quote && !quoteId) {
    return getQuote();
  }

  let cpqQuotePrices = await getQuotePricesByQuoteId(quoteId, quote);

  const productPricingMap = new Map(
    cpqQuotePrices.products.map((el) => [el.id, el])
  );

  let activeQuote : DukeActiveQuote = sessionStorage.getItem(QUOTE_KEY);

  if (activeQuote) {
    let updatedBodies = activeQuote.bodies?.map((body: BodyUnitBody) => {
      body.pricingDetails = productPricingMap.get(body.epicor_product_id);
      body.global_picks_data!.additionalData = updatePriceInGlobalPick(body.global_picks_data!.additionalData,body.pricingDetails);
      return body;
    })

    activeQuote.bodies = updatedBodies;

    activeQuote.subTotal =  cpqQuotePrices.subtotal,
    activeQuote.totalCost = cpqQuotePrices.totalCost,
    activeQuote.totalPrice =  cpqQuotePrices.totalPrice
  }

  setQuote(activeQuote);
  return activeQuote;
}

export const updatePriceInGlobalPick = (additionalData: any, pricingDetails: any) => {
  if (!pricingDetails || !pricingDetails.items) {
    return;
  }

  // Create a map from pricingDetails.items
  const priceMap = pricingDetails.items.reduce((map: Map<string, number>, item: any) => {
    const { productModelId, extNetPrice } = item;
  
    if (
      !map.has(productModelId) ||
      extNetPrice > map.get(productModelId)!
    ) {
      map.set(productModelId, extNetPrice);
    }
  
    return map;
  }, new Map<string, number>());

  // Update additionalData with the correct prices
  return additionalData.map((item :any) => {

    const productsData = appendProductPricesData(item.products, priceMap)
    return {
      ...item,
      products: productsData,
    };
  });
}

const appendProductPricesData = (products : any, priceMap : any) => {
  return products.map((item :any) => {
    const productModelId = item.product_model_id;
    const newPrice = priceMap.get(productModelId) || 0; // Default to 0 if not found

    return {
      ...item,
        price: {
          regularPrice: {
            amount: {
              currency: "USD",
              value: newPrice,
            },
          },
        },
      }
  });
}


export const quoteOperations = {
  setQuote,
  createQuote,
  getQuote,
  getQuoteName,
  setQuoteId,
  getQuoteId,
  addGlobalOptionsData,
  getGlobalOptionData,
  addBodyUnit,
  getBodyUnitByIdx,
  deleteBodyUnitByIdx,
  cloneBodyUnitByIdx,
  findBodyUnitIdxByEpicorId,
  deleteBodyUnits,
  setBodyUnits,
  deleteQuote,
  getSalesforceQuoteId,
  setSalesforceQuoteId,
  updateBodyUnit,
  updateDukeActiveQuoteBodyPosition,
  updateQuotePrices
};
export const customerOperations = {
  getLoggedInCustomerDetails,
  deleteLoggedInCustomerDetails,
  setLoggedInnCustomerDetails,
};
export const sessionStorageOperations = {
  postData,
  getData,
  deleteDataByKey,
};

export const priceOperations = {
  getPricesBySku,
  getQuotePrices,
  getBodyPricesById,
  deleteQuotePrices,
};

